import React, { useMemo, useState } from 'react';
import { scanPromotionalWords } from './TemplateDeliverabilityChecker';
import './TemplateDeliverabilityChecker.css';

const SEVERITY_ICON = { high: '✗', medium: '⚠', low: '•' };

const URL_SHORTENER_RE = /\b(?:bit\.ly|tinyurl\.com|goo\.gl|t\.co|ow\.ly|buff\.ly|cutt\.ly|rebrand\.ly|is\.gd|rb\.gy|shorturl\.at|tiny\.cc|soo\.gd|lnkd\.in)\b/i;

const stripTags = (html) => (html || '')
  .replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&[a-z0-9#]+;/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim();

// Footer-specific "inbox brain": signatures trip spam filters for very different
// reasons than the body (link stacking, image-heavy layouts, oversized logos),
// so this is scored on its own axis.
export function analyzeFooter(footer) {
  if (!footer) return null;

  const html = footer.footer_text || '';
  const plain = stripTags(html);
  const wordCount = plain ? plain.split(/\s+/).length : 0;

  const socialIcons = (footer.socialIcons || []).filter((i) => i && i.iconUrl && i.link);
  const socialLinks = socialIcons.length;
  const textLinks = (html.match(/<a\s[^>]*href\s*=/gi) || []).length;
  const optOut = !!footer.optOutEnabled;
  // Opt-out is expected and good, so it's excluded from the "link load".
  const linkLoad = textLinks + socialLinks;

  const hasLogo = !!footer.footer_image_url;
  const imageCount = (hasLogo ? 1 : 0) + socialLinks;
  const imageSize = parseInt(footer.footer_image_size, 10) || 80;

  const isEmpty = !plain && !hasLogo && socialLinks === 0;

  const spam = scanPromotionalWords(plain);

  // Gather every URL that ships in the footer to check for insecure/shortened links.
  const urls = [];
  (html.match(/href\s*=\s*["']([^"']+)["']/gi) || []).forEach((m) => {
    const mm = /href\s*=\s*["']([^"']+)["']/i.exec(m);
    if (mm && mm[1]) urls.push(mm[1]);
  });
  socialIcons.forEach((i) => urls.push(i.link));
  if (hasLogo) urls.push(footer.footer_image_url);
  const insecure = urls.filter((u) => /^http:\/\//i.test(u)).length;
  const shortened = urls.some((u) => URL_SHORTENER_RE.test(u || ''));

  const findings = [];
  const add = (severity, title, detail, weight) => findings.push({ severity, title, detail, weight });

  const linkBreakdown = `${textLinks} text link${textLinks !== 1 ? 's' : ''} + ${socialLinks} social icon${socialLinks !== 1 ? 's' : ''}`;

  // 1) Link load — the #1 footer spam trigger.
  if (linkLoad >= 5) {
    add('high', `${linkLoad} clickable links`,
      `${linkBreakdown}. Link-heavy signatures are one of the strongest footer spam signals — filters treat them like a newsletter. Keep it to 1–2 essential links.`, 3);
  } else if (linkLoad >= 3) {
    add('medium', `${linkLoad} clickable links`,
      `${linkBreakdown}. Several links in a signature push spam scoring up. Trim to the ones that really matter.`, 1.6);
  }

  // 2) A whole row of social icons = classic promotional-signature fingerprint.
  if (socialLinks >= 5) {
    add('medium', `${socialLinks} social icons`,
      'Each icon is a tiny linked image. A full row of them reads promotional to inbox providers — 2–3 is plenty.', 1.2);
  }

  // 3) Embedded image in the footer. Many inboxes (Outlook, Gmail on first
  // contact) block images by default and weight image-in-signature as a
  // promotional signal, so even a single logo costs a point or two.
  if (hasLogo) {
    const imageOnly = wordCount < 4;
    const oversized = imageSize > 220;
    let severity = 'low';
    let weight = 1.5;
    let title = 'Embedded image in footer';
    let detail = 'Lots of mail servers block images by default and treat an image-in-signature as a newsletter/promo signal. A photo in the footer can quietly hurt inbox placement — a plain-text signature is the safest.';
    if (imageOnly) {
      severity = 'medium';
      weight = 2.2;
      title = 'Image-only footer';
      detail = 'This footer is basically just an image with almost no text. Images are blocked by default in many clients and filters distrust image-only content (text can be hidden inside pixels). Add a line of plain-text contact info alongside the image.';
    } else if (oversized) {
      severity = 'medium';
      weight = 1.9;
      title = 'Large embedded image';
      detail = `An embedded ${imageSize}px image gets blocked by default in many clients and reads as promotional. Keep it small (~80–160px) or drop it for a text-only signature.`;
    }
    add(severity, title, detail, weight);
  }

  // Image-to-text ratio when several images stack up (logo + a row of icons).
  if (imageCount >= 4) {
    add('medium', `${imageCount} images total`,
      `Logo + ${socialLinks} icon image${socialLinks !== 1 ? 's' : ''}. Lots of images with little text raises the image-to-text ratio filters watch for — and every image is another thing that can be blocked.`, 1.2);
  }

  // 4) Promotional words don't belong in a signature.
  if (spam.count > 0) {
    const strong = spam.strength >= 4 || spam.count >= 3;
    add(strong ? 'high' : 'medium',
      `${spam.count} promotional word${spam.count !== 1 ? 's' : ''}`,
      `${spam.words.slice(0, 3).map((w) => `"${w}"`).join(', ')} belong in the pitch, not the signature. Sales language in a footer makes the whole mail look like an ad — keep it factual (name, company, contact).`,
      strong ? 2.5 : 1.4);
  }

  // 5) Length.
  if (wordCount > 120) {
    add('medium', 'Very long footer',
      `${wordCount} words. Long footers read like marketing boilerplate. Trim legal/marketing filler.`, 1.4);
  } else if (wordCount > 70) {
    add('low', 'Wordy footer',
      `${wordCount} words. Signatures land cleaner when short — cut what you can.`, 0.6);
  }

  // 6) Insecure & shortened links.
  if (shortened) {
    add('high', 'URL shortener in footer',
      'A shortened link hides its real destination — a strong spam/phishing signal. Use your full branded URL.', 3);
  }
  if (insecure > 0) {
    add('medium', `${insecure} insecure link${insecure !== 1 ? 's' : ''}`,
      'One or more links/images use http:// instead of https://. Non-secure resources lower trust and can trigger client warnings.', 1.4);
  }

  // 7) Shouting.
  const capsWords = plain.split(/\s+/).filter((w) => {
    const letters = w.replace(/[^A-Za-zÄÖÜ]/g, '');
    return letters.length >= 5 && w === w.toUpperCase() && /[A-ZÄÖÜ]/.test(w);
  });
  if (capsWords.length >= 2) {
    add('low', 'ALL-CAPS text',
      'Shouting words in a signature read promotional. Use normal casing.', 0.6);
  }

  // 8) Opt-out is a positive signal for outreach — nudge if it's missing.
  if (!optOut) {
    add('low', 'No unsubscribe link',
      'For outreach and bulk mail, an easy opt-out link actually improves deliverability and trust. Consider enabling it.', 0.4);
  }

  let score = 10;
  findings.forEach((f) => { score -= (f.weight || 0); });
  if (optOut) score += 0.4;
  score = Math.max(1, Math.min(10, Math.round(score)));

  return {
    score,
    findings,
    isEmpty,
    linkLoad,
    textLinks,
    socialLinks,
    imageCount,
    imageSize,
    spam,
    wordCount,
    optOut,
    insecure,
    shortened,
  };
}

const getVerdict = (s) => {
  if (s >= 7) return { status: 'ok', label: 'No Spam', color: '#22c55e' };
  if (s >= 4) return { status: 'warn', label: 'Slightly Spammy', color: '#f59e0b' };
  return { status: 'danger', label: 'Likely Spam', color: '#ef4444' };
};

const getSummary = (result) => {
  if (result.isEmpty) return 'This footer is empty — nothing here can trigger spam filters.';
  if (result.score >= 8) return 'Looks like a genuine personal signature. Nothing here should hurt your inbox placement.';
  if (result.score >= 6) return 'Low spam risk. A couple of small things below could make it read a touch promotional.';
  if (result.score >= 4) return 'Some spam risk: this signature has patterns filters associate with marketing footers. Tidy up the flags below.';
  return 'High spam risk: this footer alone can land the whole email in spam. Rework the flagged items below.';
};

const FooterFinding = ({ f }) => (
  <div className="tdc-finding">
    <span className={`tdc-finding-badge tdc-finding-badge--${f.severity}`}>{SEVERITY_ICON[f.severity]}</span>
    <div className="tdc-finding-body">
      <div className="tdc-finding-title">{f.title}</div>
      <div className="tdc-finding-detail">{f.detail}</div>
    </div>
  </div>
);

export default function FooterDeliverabilityChecker({ footer, defaultOpen = true }) {
  const result = useMemo(() => analyzeFooter(footer), [footer]);
  const [expanded, setExpanded] = useState(defaultOpen);

  if (!result) return null;

  const verdict = getVerdict(result.score);

  const linkStatus = result.linkLoad >= 5 ? 'danger' : result.linkLoad >= 3 ? 'warn' : 'ok';
  const imageStatus = result.imageCount >= 6 ? 'danger' : result.imageCount >= 4 ? 'warn' : 'ok';
  const spamStatus = result.spam.count >= 3 ? 'danger' : result.spam.count >= 1 ? 'warn' : 'ok';

  const glance = [
    { label: 'Links', value: `${result.linkLoad}`, status: linkStatus },
    { label: 'Images', value: `${result.imageCount}`, status: imageStatus },
    { label: 'Promo words', value: `${result.spam.count}`, status: spamStatus },
    { label: 'Unsubscribe', value: result.optOut ? 'On ✓' : 'Off', status: result.optOut ? 'ok' : 'warn' },
  ];

  const highCount = result.findings.filter((f) => f.severity === 'high').length;
  const mediumCount = result.findings.filter((f) => f.severity === 'medium').length;

  return (
    <div className="tdc-container">
      <div className="tdc-header" onClick={() => setExpanded(!expanded)}>
        <span className="tdc-icon"><i className="fa-solid fa-signature"></i></span>
        <span className="tdc-title">Footer Spam Check</span>
        <span className="tdc-badge" style={{ background: verdict.color }}>
          {result.score}/10 — {verdict.label}
        </span>
        <span className="tdc-chevron">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="tdc-body">
          <div className="tdc-summary">{getSummary(result)}</div>

          <div className="tdc-glance">
            {glance.map((g, i) => (
              <div key={i} className={`tdc-glance-item tdc-glance-item--${g.status}`}>
                <span className="tdc-glance-label">{g.label}</span>
                <span className="tdc-glance-value">{g.value}</span>
              </div>
            ))}
          </div>

          <div className="tdc-section">
            <div className="tdc-section-header">
              <span className="tdc-section-icon"><i className="fa-solid fa-shield-halved"></i></span>
              <div>
                <div className="tdc-section-title">
                  What inbox filters see
                  {(highCount > 0 || mediumCount > 0) && (
                    <span style={{ marginLeft: 8, fontWeight: 500, opacity: 0.7 }}>
                      {[highCount ? `${highCount} critical` : '', mediumCount ? `${mediumCount} warning${mediumCount > 1 ? 's' : ''}` : '']
                        .filter(Boolean).join(' · ')}
                    </span>
                  )}
                </div>
                <div className="tdc-section-desc">
                  Signatures often trigger spam even when the email body is clean — usually from too many links, image-heavy layouts or sales wording.
                </div>
              </div>
            </div>

            {result.findings.length === 0 ? (
              <div className="tdc-allclear">✓ No spam signals — this reads like a genuine personal signature.</div>
            ) : (
              <div className="tdc-vcard-findings">
                {result.findings.map((f, i) => <FooterFinding key={i} f={f} />)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
